/**
 * Synthesised notification sounds (Web Audio API). No audio assets so
 * the bundle stays small and the cue works offline.
 *
 * To avoid the "8-bit" feel of single sine/triangle oscillators, the
 * helpers below use additive synthesis with inharmonic bell partials
 * (loosely modelled on Risset's bell) and short low-passed noise
 * transients on percussive hits. Each partial decays at its own rate
 * and is detuned a few cents for natural beating — the result is a
 * notification cue that reads as a real instrument rather than a synth.
 *
 * Browsers gate AudioContext until a user gesture has occurred. We
 * attach one-time listeners at module load so the very first interaction
 * unlocks audio for the rest of the session — this matters because the
 * polling hook fires sounds asynchronously, long after any direct gesture.
 */

export type AlertSoundVariant =
    | 'knock-wood'
    | 'siren-wail'
    | 'siren-twotone'
    | 'whatsapp-classic'
    | 'whatsapp-pop'
    | 'slack-knock'
    | 'slack-hummus'
    | 'imperial-march'
    | 'cruel-angel'
    | 'rickroll'
    | 'aot';

export interface AlertSoundOption {
    variant: AlertSoundVariant;
    label: string;
    description: string;
}

/**
 * Variants surfaced in the dev test panel. `imperial-march` is
 * intentionally omitted — it's an easter egg triggered by the Konami
 * code on the Alerts page.
 */
export const ALERT_SOUND_OPTIONS: AlertSoundOption[] = [
    { variant: 'knock-wood', label: 'Wood knock', description: 'Three deep wood-like taps.' },
    { variant: 'slack-knock', label: 'Soft knock', description: 'Two soft taps with quick downward bend.' },
    { variant: 'siren-wail', label: 'Siren wail', description: 'Police-style up-down whoop.' },
    { variant: 'siren-twotone', label: 'Siren two-tone', description: 'European-style nee-naw.' },
    { variant: 'whatsapp-classic', label: 'Bright two-tone', description: 'Two ascending bell pings.' },
    { variant: 'whatsapp-pop', label: 'Bright pop', description: 'Single bright pop with downward bend.' },
    { variant: 'slack-hummus', label: 'Bell ding', description: 'Single warm bell ding.' },
];

/**
 * Easter-egg variants. Hidden until the user unlocks them via a secret
 * key combo (Konami / typing "eva" / typing "rick"). Once unlocked the
 * variant is persisted in localStorage and surfaced in the dropdown so
 * the user can pick it as the active notification sound.
 */
export const EASTER_EGG_OPTIONS: AlertSoundOption[] = [
    { variant: 'imperial-march', label: 'Imperial March', description: 'Star Wars theme. Unlocked via the Konami code.' },
    { variant: 'cruel-angel', label: 'Cruel Angel', description: 'Evangelion opening. Unlocked by typing "eva".' },
    { variant: 'rickroll', label: 'Rickroll', description: '8-bit Never Gonna Give You Up. Unlocked by typing "rick".' },
    { variant: 'aot', label: 'Attack on Titan', description: 'Guren no Yumiya (AoT OP1). Unlocked by typing "aot".' },
];

const DEFAULT_VARIANT: AlertSoundVariant = 'knock-wood';
const STORAGE_KEY_VARIANT = 'kontrol.alertSound.variant';
const STORAGE_KEY_MUTED = 'kontrol.alertSound.muted';
const STORAGE_KEY_UNLOCKED = 'kontrol.alertSound.unlockedEggs';

function isRegularVariant(v: string): boolean {
    return ALERT_SOUND_OPTIONS.some((o) => o.variant === v);
}

function isEasterEggVariantId(v: string): v is AlertSoundVariant {
    return EASTER_EGG_OPTIONS.some((o) => o.variant === v);
}

function isValidVariant(v: string): v is AlertSoundVariant {
    return isRegularVariant(v) || isEasterEggVariantId(v);
}

function readStoredVariant(): AlertSoundVariant {
    try {
        const v = window.localStorage.getItem(STORAGE_KEY_VARIANT);
        if (v && isValidVariant(v)) return v;
    } catch {
        /* localStorage unavailable */
    }
    return DEFAULT_VARIANT;
}

function readStoredMuted(): boolean {
    try {
        // Default to muted: when the key is absent (first load, never
        // toggled) we want silence by default. The user has to opt in
        // by clicking the speaker icon, which writes '0' here.
        const v = window.localStorage.getItem(STORAGE_KEY_MUTED);
        if (v === null) return true;
        return v === '1';
    } catch {
        return true;
    }
}

function readStoredUnlocked(): Set<AlertSoundVariant> {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY_UNLOCKED);
        if (!raw) return new Set();
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr)) return new Set();
        return new Set(arr.filter(isEasterEggVariantId));
    } catch {
        return new Set();
    }
}

let selectedVariant: AlertSoundVariant =
    typeof window !== 'undefined' ? readStoredVariant() : DEFAULT_VARIANT;
let muted: boolean = typeof window !== 'undefined' ? readStoredMuted() : false;
let unlockedEggs: Set<AlertSoundVariant> =
    typeof window !== 'undefined' ? readStoredUnlocked() : new Set();

// Cross-tab sync: when another tab/window writes to our keys, mirror
// the change into this tab's in-memory state so polling fired here
// uses the latest preference without needing a refresh.
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
        if (e.storageArea !== window.localStorage) return;
        if (e.key === STORAGE_KEY_VARIANT) {
            if (e.newValue && isValidVariant(e.newValue)) {
                selectedVariant = e.newValue;
            } else if (e.newValue === null) {
                selectedVariant = DEFAULT_VARIANT;
            }
        } else if (e.key === STORAGE_KEY_MUTED) {
            muted = e.newValue === '1';
        } else if (e.key === STORAGE_KEY_UNLOCKED) {
            unlockedEggs = readStoredUnlocked();
        }
    });
}

export function getSelectedAlertSoundVariant(): AlertSoundVariant {
    return selectedVariant;
}

export function setSelectedAlertSoundVariant(v: AlertSoundVariant): void {
    selectedVariant = v;
    try {
        window.localStorage.setItem(STORAGE_KEY_VARIANT, v);
    } catch {
        /* localStorage unavailable */
    }
}

export function isAlertSoundMuted(): boolean {
    return muted;
}

export function setAlertSoundMuted(m: boolean): void {
    muted = m;
    try {
        window.localStorage.setItem(STORAGE_KEY_MUTED, m ? '1' : '0');
    } catch {
        /* localStorage unavailable */
    }
}

/**
 * Mark an easter-egg variant as discovered. Returns `true` if this is
 * the first time the user unlocks it (caller can show a celebration);
 * returns `false` if it was already unlocked. Non-easter-egg variants
 * are rejected.
 */
export function unlockEasterEgg(variant: AlertSoundVariant): boolean {
    if (!isEasterEggVariantId(variant)) return false;
    if (unlockedEggs.has(variant)) return false;
    unlockedEggs = new Set(unlockedEggs);
    unlockedEggs.add(variant);
    try {
        window.localStorage.setItem(
            STORAGE_KEY_UNLOCKED,
            JSON.stringify([...unlockedEggs]),
        );
    } catch {
        /* localStorage unavailable */
    }
    return true;
}

export function getUnlockedEasterEggs(): AlertSoundOption[] {
    return EASTER_EGG_OPTIONS.filter((o) => unlockedEggs.has(o.variant));
}

export function isEasterEggUnlocked(variant: AlertSoundVariant): boolean {
    return unlockedEggs.has(variant);
}

/**
 * Some easter-egg variants play real audio files (clipped to a fixed
 * length) instead of being synthesised, because the original songs are
 * worth hearing. The file is served from `public/easteregg/`. Other
 * variants fall through to the Web Audio synth path.
 */
const AUDIO_FILES: Partial<Record<AlertSoundVariant, string>> = {
    'cruel-angel': '/easteregg/eva.opus',
    aot: '/easteregg/aot.opus',
};
const AUDIO_FILE_DURATION_MS = 15_000;
const AUDIO_FILE_VOLUME = 0.55;

let audioContext: AudioContext | null = null;
let unlocked = false;
// Tracks anything currently making sound so the next play can stop it
// first — prevents the previous sound from running while a new one is
// triggered. `synthMasterGain` is the destination that every synth
// oscillator/noise source routes through; disconnecting it instantly
// silences anything still scheduled.
let synthMasterGain: GainNode | null = null;
let activeAudioElement: HTMLAudioElement | null = null;
let activeAudioStopTimer: ReturnType<typeof setTimeout> | null = null;

function getContext(): AudioContext | null {
    if (audioContext) return audioContext;
    const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;
    if (!Ctor) return null;
    audioContext = new Ctor();
    return audioContext;
}

function getSynthMasterGain(ctx: AudioContext): GainNode {
    if (!synthMasterGain) {
        synthMasterGain = ctx.createGain();
        synthMasterGain.connect(ctx.destination);
    }
    return synthMasterGain;
}

function stopAllSounds(): void {
    if (activeAudioElement) {
        try {
            activeAudioElement.pause();
            activeAudioElement.currentTime = 0;
        } catch {
            /* ignore */
        }
        activeAudioElement = null;
    }
    if (activeAudioStopTimer !== null) {
        clearTimeout(activeAudioStopTimer);
        activeAudioStopTimer = null;
    }
    if (synthMasterGain) {
        try {
            synthMasterGain.disconnect();
        } catch {
            /* ignore */
        }
        synthMasterGain = null;
    }
}

function playAudioFile(url: string): void {
    const audio = new Audio(url);
    audio.volume = AUDIO_FILE_VOLUME;
    audio.currentTime = 0;
    activeAudioElement = audio;
    void audio.play().catch(() => {
        /* autoplay blocked or file missing */
    });
    activeAudioStopTimer = setTimeout(() => {
        if (activeAudioElement === audio) {
            try {
                audio.pause();
            } catch {
                /* ignore */
            }
            activeAudioElement = null;
        }
        activeAudioStopTimer = null;
    }, AUDIO_FILE_DURATION_MS);
}

function unlockAudio() {
    if (unlocked) return;
    unlocked = true;
    const ctx = getContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
        void ctx.resume().catch(() => {
            unlocked = false;
        });
    }
}

if (typeof window !== 'undefined') {
    const opts: AddEventListenerOptions = { once: true, passive: true };
    window.addEventListener('pointerdown', unlockAudio, opts);
    window.addEventListener('keydown', unlockAudio, opts);
    window.addEventListener('touchstart', unlockAudio, opts);
}

/**
 * Inharmonic bell partials. Each entry: ratio over the fundamental,
 * peak amplitude (relative), decay factor (1 = full duration, lower =
 * partial dies sooner), and a small detune in cents for natural beating.
 * The ratios 2.4, 4.2 and 5.4 are intentionally non-integer — that's
 * what makes the result read as a real metallic body rather than a
 * synth. Higher partials carry less amplitude and decay faster, which
 * matches the spectrum of a struck bell.
 */
const BELL_PARTIALS: Array<{
    ratio: number;
    amp: number;
    decay: number;
    detuneCents: number;
}> = [
    { ratio: 1.0, amp: 1.0, decay: 1.0, detuneCents: 0 },
    { ratio: 2.0, amp: 0.55, decay: 0.85, detuneCents: 3 },
    { ratio: 2.4, amp: 0.3, decay: 0.7, detuneCents: -3 },
    { ratio: 3.0, amp: 0.22, decay: 0.6, detuneCents: 2 },
    { ratio: 4.2, amp: 0.12, decay: 0.4, detuneCents: -4 },
    { ratio: 5.4, amp: 0.07, decay: 0.3, detuneCents: 4 },
];

interface NoiseTransientSpec {
    /** Total duration of the noise burst in seconds. */
    duration: number;
    /** Peak gain of the burst (0..1). */
    volume: number;
    /** Low-pass cutoff in Hz; controls the body of the click. */
    lowpassHz: number;
}

interface ToneSpec {
    frequency: number;
    /** Seconds after sound start. */
    offset: number;
    /** Total seconds the tone is audible. */
    duration: number;
    /** Peak gain (0..1). */
    volume?: number;
    /** Wave shape; defaults to triangle. Ignored when `bell` is true. */
    type?: OscillatorType;
    /** Render this tone as an additive bell using BELL_PARTIALS. */
    bell?: boolean;
    /** Frequency ramp targets relative to the tone start. Each entry
     *  linearly ramps the oscillator frequency to `freq` by `atOffset`
     *  seconds. Used by sirens and pitched percussion. */
    freqRamp?: { freq: number; atOffset: number }[];
    /** When true, hold the peak gain for most of the duration instead of
     *  decaying exponentially. Used by sustained tones (sirens, brass). */
    sustain?: boolean;
    /** Optional low-passed white-noise transient at the same offset.
     *  Adds the percussive "click" that distinguishes a real knock from
     *  a pure tone. */
    transient?: NoiseTransientSpec;
    /** Pitch detune in cents applied for the entire tone. Used to layer
     *  several copies of the same shape with slight tuning differences
     *  for chorus/thickness on sustained tones (sirens, brass). */
    detuneCents?: number;
}

function playSinglePartial(
    ctx: AudioContext,
    start: number,
    frequency: number,
    duration: number,
    peak: number,
    type: OscillatorType,
    sustain: boolean,
    detuneCents: number,
    freqRamp?: { freq: number; atOffset: number }[]
) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, start);
    if (detuneCents) osc.detune.setValueAtTime(detuneCents, start);
    if (freqRamp) {
        for (const r of freqRamp) {
            osc.frequency.linearRampToValueAtTime(r.freq, start + r.atOffset);
        }
    }
    osc.connect(gain).connect(getSynthMasterGain(ctx));

    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(peak, start + 0.005);
    if (sustain) {
        gain.gain.setValueAtTime(peak, start + Math.max(0.01, duration - 0.05));
        gain.gain.linearRampToValueAtTime(0, start + duration);
    } else {
        gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    }

    osc.start(start);
    osc.stop(start + duration);
}

function playNoiseTransient(
    ctx: AudioContext,
    start: number,
    spec: NoiseTransientSpec
) {
    const len = Math.max(1, Math.floor(ctx.sampleRate * spec.duration));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i += 1) {
        data[i] = Math.random() * 2 - 1;
    }

    const src = ctx.createBufferSource();
    src.buffer = buf;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = spec.lowpassHz;
    filter.Q.value = 1;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(spec.volume, start + 0.001);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + spec.duration);

    src.connect(filter).connect(gain).connect(getSynthMasterGain(ctx));
    src.start(start);
    src.stop(start + spec.duration);
}

function playTones(ctx: AudioContext, tones: ToneSpec[]): void {
    const baseTime = ctx.currentTime;
    for (const t of tones) {
        const start = baseTime + t.offset;
        const peak = t.volume ?? 0.18;

        if (t.transient) {
            playNoiseTransient(ctx, start, t.transient);
        }

        if (t.bell) {
            // Additive synthesis: each inharmonic partial is its own
            // oscillator with its own envelope. Lower partials carry
            // most of the energy, higher partials add metallic ring.
            for (const p of BELL_PARTIALS) {
                playSinglePartial(
                    ctx,
                    start,
                    t.frequency * p.ratio,
                    t.duration * p.decay,
                    peak * p.amp,
                    'sine',
                    false,
                    p.detuneCents,
                );
            }
        } else {
            playSinglePartial(
                ctx,
                start,
                t.frequency,
                t.duration,
                peak,
                t.type ?? 'triangle',
                t.sustain ?? false,
                t.detuneCents ?? 0,
                t.freqRamp,
            );
        }
    }
}

// Pitch references for the Imperial March (Western tuning):
//   G4 = 392 Hz, Eb4 = 311.13 Hz, Bb4 = 466.16 Hz
const PITCH_G4 = 392;
const PITCH_EB4 = 311.13;
const PITCH_BB4 = 466.16;

function tonesFor(variant: AlertSoundVariant): ToneSpec[] {
    switch (variant) {
        case 'knock-wood':
            // Three low-pitched thumps. The `transient` adds the
            // initial knuckle-on-wood click; the freqRamp gives the
            // body its small downward bend so each tap reads as
            // physical contact rather than a synth blip.
            return [
                {
                    frequency: 200, offset: 0, duration: 0.09,
                    type: 'sine', volume: 0.32,
                    freqRamp: [{ freq: 110, atOffset: 0.09 }],
                    transient: { duration: 0.03, volume: 0.35, lowpassHz: 1800 },
                },
                {
                    frequency: 200, offset: 0.13, duration: 0.09,
                    type: 'sine', volume: 0.32,
                    freqRamp: [{ freq: 110, atOffset: 0.09 }],
                    transient: { duration: 0.03, volume: 0.35, lowpassHz: 1800 },
                },
                {
                    frequency: 200, offset: 0.26, duration: 0.09,
                    type: 'sine', volume: 0.32,
                    freqRamp: [{ freq: 110, atOffset: 0.09 }],
                    transient: { duration: 0.03, volume: 0.35, lowpassHz: 1800 },
                },
            ];
        case 'siren-wail': {
            // Police-style wail. Three sawtooths (main + ±11 cents) form
            // a chorus that thickens the timbre into a real horn array
            // instead of a single sine modulator. A square sub-octave
            // adds low-end body. Sweep is asymmetric (rises a little
            // slower than it falls) to match the cadence of an actual
            // emergency vehicle wail.
            const sweep = (peak: number) => [
                { freq: peak, atOffset: 0.55 },
                { freq: peak / (1300 / 600), atOffset: 1.0 },
            ];
            return [
                {
                    frequency: 600, offset: 0, duration: 1.0,
                    type: 'sawtooth', volume: 0.08, sustain: true,
                    freqRamp: sweep(1300),
                },
                {
                    frequency: 600, offset: 0, duration: 1.0,
                    type: 'sawtooth', volume: 0.06, sustain: true,
                    detuneCents: 11,
                    freqRamp: sweep(1300),
                },
                {
                    frequency: 600, offset: 0, duration: 1.0,
                    type: 'sawtooth', volume: 0.06, sustain: true,
                    detuneCents: -11,
                    freqRamp: sweep(1300),
                },
                {
                    frequency: 300, offset: 0, duration: 1.0,
                    type: 'square', volume: 0.04, sustain: true,
                    freqRamp: sweep(650),
                },
            ];
        }
        case 'siren-twotone':
            return [
                { frequency: 880, offset: 0, duration: 0.2, type: 'sawtooth', volume: 0.12, sustain: true },
                { frequency: 587, offset: 0.22, duration: 0.2, type: 'sawtooth', volume: 0.12, sustain: true },
            ];
        case 'whatsapp-classic':
            // Iconic two-tone ping using the additive bell (real
            // metallic ring instead of a flat triangle wave).
            return [
                { frequency: 1700, offset: 0, duration: 0.18, volume: 0.16, bell: true },
                { frequency: 1900, offset: 0.08, duration: 0.22, volume: 0.16, bell: true },
            ];
        case 'whatsapp-pop':
            // Single bell pop. No freqRamp here — the inharmonic
            // partials already give the character we want; bending the
            // fundamental would smear the bell ring.
            return [
                { frequency: 1100, offset: 0, duration: 0.28, volume: 0.2, bell: true },
            ];
        case 'slack-knock':
            // Slack-style knock: percussive transient (the actual
            // "click" your ear listens for) plus a short additive bell
            // body that gives the residual mid-pitch ring you remember
            // from the real "Knock Brush" sound.
            return [
                {
                    frequency: 820, offset: 0, duration: 0.09,
                    volume: 0.22, bell: true,
                    transient: { duration: 0.018, volume: 0.32, lowpassHz: 4200 },
                },
                {
                    frequency: 820, offset: 0.11, duration: 0.09,
                    volume: 0.22, bell: true,
                    transient: { duration: 0.018, volume: 0.32, lowpassHz: 4200 },
                },
            ];
        case 'slack-hummus':
            return [
                { frequency: 1175, offset: 0, duration: 0.45, volume: 0.18, bell: true },
            ];
        case 'imperial-march':
            // First phrase: G G G Eb. Bb G Eb. Bb G
            return [
                { frequency: PITCH_G4, offset: 0.0, duration: 0.45, type: 'sawtooth', volume: 0.16, sustain: true },
                { frequency: PITCH_G4, offset: 0.55, duration: 0.45, type: 'sawtooth', volume: 0.16, sustain: true },
                { frequency: PITCH_G4, offset: 1.10, duration: 0.45, type: 'sawtooth', volume: 0.16, sustain: true },
                { frequency: PITCH_EB4, offset: 1.65, duration: 0.4, type: 'sawtooth', volume: 0.16, sustain: true },
                { frequency: PITCH_BB4, offset: 2.05, duration: 0.13, type: 'sawtooth', volume: 0.16, sustain: true },
                { frequency: PITCH_G4, offset: 2.18, duration: 0.45, type: 'sawtooth', volume: 0.16, sustain: true },
                { frequency: PITCH_EB4, offset: 2.73, duration: 0.4, type: 'sawtooth', volume: 0.16, sustain: true },
                { frequency: PITCH_BB4, offset: 3.13, duration: 0.13, type: 'sawtooth', volume: 0.16, sustain: true },
                { frequency: PITCH_G4, offset: 3.26, duration: 0.8, type: 'sawtooth', volume: 0.16, sustain: true },
            ];
        case 'cruel-angel': {
            // "A Cruel Angel's Thesis" (Evangelion OP). Synth-pop lead
            // (sawtooth with ±9 ¢ detune for chorus thickness — the kind
            // of timbre the original 1995 production used) plus a quiet
            // sub-octave triangle bass that follows the downbeats. Note
            // sequence is the kalimba tab "6 1° 2° 1° 2° 2° 2° 5° 4° 3°
            // 2° 3° 3° 5° 6° 2° 1° 5° 5° 3° 5° 5° 6°" re-flatted to C
            // minor, plus an extended chorus climax. ~15 s.
            const lead = 0.10;
            const bass = 0.07;
            const Ab3 = 207.65, C4 = 261.63, G4 = 392.00;
            const Ab4 = 415.30, Bb4 = 466.16;
            const C5 = 523.25, D5 = 587.33, Eb5 = 622.25, F5 = 698.46;
            const G5 = 783.99, Ab5 = 830.61, Bb5 = 932.33, C6 = 1046.50;
            // Layered sawtooths give the synth-lead body; ±9 cents
            // detune produces the slight beating of a polysynth ensemble
            // patch.
            const note = (frequency: number, offset: number, duration: number): ToneSpec[] => [
                { frequency, offset, duration, type: 'sawtooth', volume: lead, sustain: true },
                { frequency, offset, duration, type: 'sawtooth', volume: lead * 0.7, sustain: true, detuneCents: 9 },
                { frequency, offset, duration, type: 'sawtooth', volume: lead * 0.7, sustain: true, detuneCents: -9 },
            ];
            const bassNote = (frequency: number, offset: number, duration: number): ToneSpec => ({
                frequency, offset, duration, type: 'triangle', volume: bass, sustain: true,
            });
            return [
                // Verse line 1 — "zan-ko-ku-na ten-shi no you ni"
                ...note(Ab4, 0.00, 0.30),
                ...note(C5, 0.32, 0.20),
                ...note(D5, 0.54, 0.20),
                ...note(C5, 0.76, 0.20),
                ...note(D5, 0.98, 0.30),
                ...note(D5, 1.30, 0.20),
                ...note(D5, 1.52, 0.30),
                ...note(G5, 1.84, 0.40),
                ...note(F5, 2.26, 0.30),
                ...note(Eb5, 2.58, 0.30),
                ...note(D5, 2.90, 0.20),
                ...note(Eb5, 3.12, 0.55),
                bassNote(Ab3, 0.00, 0.80),
                bassNote(C4, 0.85, 0.80),
                bassNote(G4, 1.70, 0.80),
                bassNote(C4, 2.55, 1.10),
                // Verse line 2 — "shou-nen yo shin-wa ni na-re"
                ...note(Eb5, 3.75, 0.30),
                ...note(G5, 4.07, 0.40),
                ...note(Ab5, 4.50, 0.50),
                ...note(D5, 5.05, 0.30),
                ...note(C5, 5.38, 0.30),
                ...note(G5, 5.70, 0.40),
                ...note(G5, 6.13, 0.30),
                ...note(Eb5, 6.45, 0.30),
                ...note(G5, 6.78, 0.30),
                ...note(G5, 7.10, 0.40),
                ...note(Ab5, 7.55, 0.55),
                bassNote(C4, 3.75, 0.80),
                bassNote(Ab3, 4.60, 0.80),
                bassNote(C4, 5.45, 0.80),
                bassNote(G4, 6.30, 0.80),
                bassNote(C4, 7.15, 1.00),
                // Bridge — "aoi kaze ga ima"
                ...note(G5, 8.20, 0.25),
                ...note(F5, 8.48, 0.25),
                ...note(G5, 8.76, 0.30),
                ...note(Ab5, 9.10, 0.30),
                ...note(Bb5, 9.43, 0.40),
                ...note(Ab5, 9.86, 0.30),
                ...note(G5, 10.18, 0.30),
                ...note(F5, 10.50, 0.30),
                ...note(Eb5, 10.82, 0.55),
                bassNote(C4, 8.20, 0.80),
                bassNote(Ab3, 9.05, 0.80),
                bassNote(C4, 9.90, 0.80),
                bassNote(Bb4, 10.75, 0.85),
                // Chorus climax — "shinwa ni na-re THE-SIS"
                ...note(G5, 11.40, 0.30),
                ...note(Bb5, 11.73, 0.30),
                ...note(C6, 12.05, 0.40),
                ...note(Bb5, 12.48, 0.30),
                ...note(Ab5, 12.80, 0.30),
                ...note(G5, 13.12, 0.30),
                ...note(F5, 13.44, 0.30),
                ...note(Eb5, 13.76, 0.30),
                ...note(D5, 14.08, 0.30),
                ...note(C5, 14.40, 1.00),
                bassNote(C4, 11.40, 0.80),
                bassNote(G4, 12.25, 0.80),
                bassNote(Ab3, 13.10, 0.80),
                bassNote(C4, 13.95, 1.50),
            ];
        }
        case 'aot': {
            // "Guren no Yumiya" (Attack on Titan OP1, Linked Horizon).
            // Synth-pop lead (sawtooth × 3 with ±9 ¢ detune) over a
            // sub-octave triangle bass — approximates the dramatic
            // orchestral / synth feel of the original. Notes follow the
            // C# minor pianoletternotes transcription. ~15 s.
            const lead = 0.10;
            const bass = 0.07;
            const Cs3 = 138.59, E3 = 164.81, B3 = 246.94;
            const B4 = 493.88, Cs5 = 554.37, D5 = 587.33, Ds5 = 622.25;
            const E5 = 659.25, Fs5 = 739.99;
            const G5 = 783.99, Gs5 = 830.61, A5 = 880.00;
            const note = (frequency: number, offset: number, duration: number): ToneSpec[] => [
                { frequency, offset, duration, type: 'sawtooth', volume: lead, sustain: true },
                { frequency, offset, duration, type: 'sawtooth', volume: lead * 0.7, sustain: true, detuneCents: 9 },
                { frequency, offset, duration, type: 'sawtooth', volume: lead * 0.7, sustain: true, detuneCents: -9 },
            ];
            const bassNote = (frequency: number, offset: number, duration: number): ToneSpec => ({
                frequency, offset, duration, type: 'triangle', volume: bass, sustain: true,
            });
            return [
                // "Sie sind das Es-sen!" — C# C# E D# D#
                ...note(Cs5, 0.00, 0.20),
                ...note(Cs5, 0.22, 0.20),
                ...note(E5, 0.45, 0.20),
                ...note(Ds5, 0.68, 0.20),
                ...note(Ds5, 0.90, 0.50),
                // "Und wir sind die Jä-ger!" — C# C# E D# (held)
                ...note(Cs5, 1.50, 0.20),
                ...note(Cs5, 1.72, 0.20),
                ...note(E5, 1.95, 0.20),
                ...note(Ds5, 2.18, 0.60),
                // Ascending bridge — G# E F# D# E C#
                ...note(Gs5, 2.90, 0.25),
                ...note(E5, 3.18, 0.20),
                ...note(Fs5, 3.40, 0.25),
                ...note(Ds5, 3.68, 0.20),
                ...note(E5, 3.90, 0.20),
                ...note(Cs5, 4.12, 0.45),
                bassNote(Cs3, 0.00, 1.40),
                bassNote(Cs3, 1.50, 1.40),
                bassNote(B3, 2.90, 1.65),
                // Verse continuation — D# G# E F# D# E D# C#
                ...note(Ds5, 4.65, 0.25),
                ...note(Gs5, 4.93, 0.25),
                ...note(E5, 5.21, 0.20),
                ...note(Fs5, 5.43, 0.25),
                ...note(Ds5, 5.71, 0.20),
                ...note(E5, 5.93, 0.20),
                ...note(Ds5, 6.15, 0.20),
                ...note(Cs5, 6.37, 0.50),
                bassNote(Cs3, 4.65, 1.05),
                bassNote(E3, 5.75, 1.10),
                // Chorus iconic line — F# G E F# D B G A
                ...note(Fs5, 7.00, 0.25),
                ...note(G5, 7.28, 0.25),
                ...note(E5, 7.56, 0.20),
                ...note(Fs5, 7.78, 0.25),
                ...note(D5, 8.06, 0.25),
                ...note(B4, 8.34, 0.25),
                ...note(G5, 8.62, 0.25),
                ...note(A5, 8.90, 0.40),
                bassNote(Cs3, 7.00, 1.30),
                bassNote(B3, 8.35, 0.95),
                // Climax descending — F# G F# E D# (held)
                ...note(Fs5, 9.40, 0.20),
                ...note(G5, 9.62, 0.20),
                ...note(Fs5, 9.84, 0.20),
                ...note(E5, 10.06, 0.20),
                ...note(Ds5, 10.28, 0.60),
                bassNote(E3, 9.40, 1.55),
                // Reprise of opening hook
                ...note(Cs5, 11.00, 0.20),
                ...note(Cs5, 11.22, 0.20),
                ...note(E5, 11.45, 0.20),
                ...note(Ds5, 11.68, 0.20),
                ...note(Ds5, 11.90, 0.50),
                ...note(Cs5, 12.50, 0.20),
                ...note(Cs5, 12.72, 0.20),
                ...note(E5, 12.95, 0.20),
                ...note(Ds5, 13.18, 0.55),
                bassNote(Cs3, 11.00, 1.45),
                bassNote(Cs3, 12.50, 1.30),
                // Final cadence — G# F# E C#
                ...note(Gs5, 13.85, 0.30),
                ...note(Fs5, 14.18, 0.30),
                ...note(E5, 14.50, 0.30),
                ...note(Cs5, 14.83, 1.10),
                bassNote(Cs3, 13.85, 2.10),
            ];
        }
        case 'rickroll': {
            // 8-bit "Never Gonna Give You Up". Notes match the original
            // chorus melody in D major (transcribed from noobnotes):
            //   "give you up"   = A B D B F# F# E
            //   "let you down"  = A B D B E E D C# B
            //   "desert you"    = A B D B D E C# A A E D
            // Square wave for the Game Boy / chiptune feel.
            const v = 0.14;
            const A4 = 440.00, B4 = 493.88, Cs5 = 554.37;
            const D5 = 587.33, E5 = 659.25, Fs5 = 739.99, A5 = 880.00;
            const sq = (frequency: number, offset: number, duration: number): ToneSpec => ({
                frequency, offset, duration,
                type: 'square', volume: v, sustain: true,
            });
            return [
                // "Never gon-na give you up" — A B D B F# F# E
                sq(A4, 0.00, 0.18),
                sq(B4, 0.20, 0.18),
                sq(D5, 0.40, 0.20),
                sq(B4, 0.62, 0.20),
                sq(Fs5, 0.85, 0.30),
                sq(Fs5, 1.18, 0.30),
                sq(E5, 1.50, 0.55),
                // "Never gon-na let you down" — A B D B E E D C# B
                sq(A4, 2.15, 0.18),
                sq(B4, 2.35, 0.18),
                sq(D5, 2.55, 0.20),
                sq(B4, 2.78, 0.20),
                sq(E5, 3.00, 0.30),
                sq(E5, 3.33, 0.30),
                sq(D5, 3.65, 0.20),
                sq(Cs5, 3.88, 0.20),
                sq(B4, 4.10, 0.55),
                // "Never gon-na run a-round and de-sert you" — A B D B D E C# A A E D
                sq(A4, 4.75, 0.18),
                sq(B4, 4.95, 0.18),
                sq(D5, 5.15, 0.20),
                sq(B4, 5.38, 0.20),
                sq(D5, 5.60, 0.20),
                sq(E5, 5.82, 0.20),
                sq(Cs5, 6.05, 0.20),
                sq(A4, 6.27, 0.20),
                sq(A4, 6.50, 0.20),
                sq(E5, 6.72, 0.20),
                sq(D5, 6.95, 0.55),
                // "Never gon-na make you cry" — A B D B F# F# E (same as first)
                sq(A4, 7.60, 0.18),
                sq(B4, 7.80, 0.18),
                sq(D5, 8.00, 0.20),
                sq(B4, 8.22, 0.20),
                sq(Fs5, 8.45, 0.30),
                sq(Fs5, 8.78, 0.30),
                sq(E5, 9.10, 0.55),
                // "Never gon-na say good-bye" — A B D B A C# D C# B
                sq(A4, 9.75, 0.18),
                sq(B4, 9.95, 0.18),
                sq(D5, 10.15, 0.20),
                sq(B4, 10.38, 0.20),
                sq(A5, 10.60, 0.30),
                sq(Cs5, 10.93, 0.20),
                sq(D5, 11.15, 0.20),
                sq(Cs5, 11.38, 0.20),
                sq(B4, 11.60, 0.70),
            ];
        }
    }
}

function playInternal(variant: AlertSoundVariant): void {
    // Always stop whatever is currently playing first — without this a
    // long easter-egg track keeps running while a new notification is
    // triggered, and they audibly overlap.
    stopAllSounds();

    const fileUrl = AUDIO_FILES[variant];
    if (fileUrl) {
        playAudioFile(fileUrl);
        return;
    }

    try {
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') {
            void ctx.resume();
        }
        playTones(ctx, tonesFor(variant));
    } catch {
        /* Audio blocked (e.g. autoplay policy before user gesture) */
    }
}

/**
 * Play the user's currently selected alert sound. Skipped when muted.
 * Used by the polling code; respects user preferences end-to-end.
 */
export function playAlertSound(): void {
    if (muted) return;
    playInternal(selectedVariant);
}

/**
 * Play a specific variant ignoring the user's selection AND the mute
 * state. Used by the dropdown previews and the Konami easter egg, where
 * the click is itself the consent to play.
 */
export function previewAlertSound(variant: AlertSoundVariant): void {
    playInternal(variant);
}
